//@flow
import {IconButton, makeStyles} from '@material-ui/core';
import React, {useEffect, useState} from 'react';
import {colors} from '../../../../../../assets/styles/colors';
import CropModal from '../../../../../../elements/CropModal';
import type {ImageToCrop} from '../../../../../../elements/CropModal';
import {useFormContext} from 'react-hook-form';
import {QUICK_REPLY_FIELDS} from '../constants';
import Toast from '../../../../../../elements/Toast';
import CameraIcon from '../../../../../../elements/icons/CameraIcon';
import {CloseOutlined} from '@material-ui/icons';
import ImageRenderer from '../../../../../../elements/ImageRenderer';
import FileTypeIcons from '../../../../../../elements/FileTypeIcons';
import {
  extractFileData,
  getLabelFromMimetype,
} from '../../../../../../utils/file';
import {getFileUrl} from '../../../../../../utils/image_provider';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '.75rem',
    flexDirection: 'column',
    gap: '.5rem',
    borderRadius: '.5rem',
    position: 'relative',
    background: 'white',
    border: `1px dashed ${colors.grey400}`,
    cursor: 'pointer',
    height: '150px',

    '&:focus-within': {
      borderColor: colors.primary,
    },
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '.5rem',
    fontSize: '.9rem',
    color: colors.grey800,
  },
  preview: {
    width: 'auto',
    height: '100%',
    objectFit: 'contain',
    position: 'absolute',
  },
  input: {
    position: 'absolute',
    width: 0,
    height: 0,
    zIndex: -1,
  },
  helpText: {
    fontSize: '.75rem',
    color: colors.grey600,
  },

  attachmentPreview: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    width: '4rem',
    height: '4rem',
    minWidth: '4rem',
    minHeight: '4rem',
    borderRadius: '.5rem',
    overflow: 'hidden',
    background: colors.grey200,
    fontSize: '1.25rem',
    flexDirection: 'column',
    gap: '.25rem',

    '&[data-document]': {
      minWidth: '6rem',
      paddingTop: '.25rem',
      width: '6rem',
    },

    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
  },
  attachmentPreviewName: {
    fontSize: '.75rem',
    textAlign: 'center',
    width: '95%',
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  attachmentPreviewRemove: {
    position: 'absolute',
    top: 0,
    right: 0,
    padding: '.25rem',
    background: 'white',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    fontSize: '.5rem',
  },
  addAttachment: {
    width: '4rem',
    height: '4rem',
    minWidth: '4rem',
    minHeight: '4rem',
    padding: '0',
    borderRadius: '.5rem',
    background: colors.grey200,

    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

const maxFileSize = 1024 * 1024 * 5;

type Props = {
  savedImage?: string,
};

type SavedFile = {
  name: string,
  type: string,
  src: string,
};

export default function AttachmentField({savedImage}: Props) {
  const classes = useStyles();

  const {
    register,
    setValue,
    clearErrors,
    setError,
    errors,
    watch,
  } = useFormContext();

  const [savedFile, setSavedFile] = useState<SavedFile | null>(null);

  useEffect(() => {
    register(QUICK_REPLY_FIELDS.files);
  }, [register]);

  const fileIdSaved = watch(QUICK_REPLY_FIELDS.fileId);

  useEffect(() => {
    if (!fileIdSaved) return;
    const url = getFileUrl(fileIdSaved);
    extractFileData(url).then(result => {
      const {error, type} = result;
      if (error) return;
      setSavedFile({
        name: getLabelFromMimetype(type),
        type: type,
        src: url,
      });
    });
  }, [fileIdSaved]);

  const [file, setFile] = useState<File | null>(null);
  const [imageResult, setImageResult] = useState<ImageToCrop | null>(null);

  const handleFileChange = (e: Event) => {
    //$FlowFixMe
    const input: HTMLInputElement = e.target;
    setImageResult(null);

    if (!input.files) return;
    const [file] = input.files;

    if (file.size > maxFileSize) {
      setError(QUICK_REPLY_FIELDS.files, {
        type: 'manual',
        message: 'El tamaño máximo del archivo es de 5MB',
      });
      return;
    }

    setFile(file);
    setValue(QUICK_REPLY_FIELDS.fileId, null);
    setValue(QUICK_REPLY_FIELDS.fileName, file.name);
    setSavedFile(null);

    if (!file.type.includes('image')) {
      setValue(QUICK_REPLY_FIELDS.files, file);
    }
  };

  const imagePreview = (imageResult && imageResult.preview) || savedImage;

  return (
    <>
      <label className={classes.root}>
        {imagePreview ? (
          <img className={classes.preview} src={imagePreview} alt="" />
        ) : (
          <>
            <span className={classes.label}>
              <CameraIcon />
              Agregar video o imágen
            </span>
            <span className={classes.helpText}>
              JPG, PNG o MOV, MP4. 5mb max.
            </span>
          </>
        )}
        <input
          type="file"
          className={classes.input}
          onChange={handleFileChange}
        />
      </label>

      {file && file.type.includes('image') && (
        <CropModal
          open
          onClose={() => setFile(null)}
          onSubmit={results => {
            const [result] = results;
            setImageResult(result);
            setValue(QUICK_REPLY_FIELDS.files, result.file);
            setFile(null);
          }}
          images={[
            {
              file: file,
              preview: URL.createObjectURL(file),
            },
          ]}
        />
      )}

      {file && (
        <AttachmentFileItem
          name={file.name}
          type={file.type}
          src={file}
          onRemove={() => {
            setFile(null);
            setValue(QUICK_REPLY_FIELDS.files, null);
          }}
        />
      )}

      {savedFile && !imagePreview && (
        <AttachmentFileItem
          name={savedFile.name}
          type={savedFile.type}
          src={savedFile.type}
          onRemove={() => {
            setValue(QUICK_REPLY_FIELDS.fileId, null);
            setSavedFile(null);
          }}
        />
      )}

      <Toast
        open={!!errors[QUICK_REPLY_FIELDS.files]}
        onClose={() => {
          clearErrors(QUICK_REPLY_FIELDS.files);
        }}
        closeDelay={5000}
        severity="error"
      >
        {errors[QUICK_REPLY_FIELDS.files] &&
          errors[QUICK_REPLY_FIELDS.files].message}
      </Toast>
    </>
  );
}

type AttachmentFileItemProps = {
  src: File | string,
  name: string,
  type: string,
  onRemove: () => void,
};
function AttachmentFileItem(props: AttachmentFileItemProps) {
  const classes = useStyles();

  const {src, onRemove, type, name} = props;

  const isImage = type.includes('image');

  if (isImage) {
    return (
      <picture className={classes.attachmentPreview}>
        <IconButton
          className={classes.attachmentPreviewRemove}
          size="small"
          onClick={onRemove}
        >
          <CloseOutlined fontSize="small" />
        </IconButton>

        <ImageRenderer src={src} alt={name} />
      </picture>
    );
  }

  return (
    <div className={classes.attachmentPreview} data-document>
      <IconButton
        className={classes.attachmentPreviewRemove}
        size="small"
        onClick={onRemove}
      >
        <CloseOutlined fontSize="small" />
      </IconButton>

      <FileTypeIcons type={type} />

      <span className={classes.attachmentPreviewName}>{name}</span>
    </div>
  );
}
